import { Controller } from "@hotwired/stimulus"

// Manages the unified appointment scheduling drawer:
// - Derives end time from start time + appointment type duration
// - Shows/hides person fields based on combobox selection (like lead-form)
// - Updates derived end time when start time or appointment type changes
export default class extends Controller {
  static targets = [
    "startTime",
    "endTimeDisplay",
    "memberFields",
    "appointmentTypeSelect",
    "dateInput",
  ]

  static values = {
    durationMinutes: { type: Number, default: 60 },
    timezone: { type: String, default: "UTC" },
  }

  connect() {
    this.updateEndTime()
    this.applyDurationFromSelect()
  }

  // When appointment type changes, duration may change
  appointmentTypeSelectTargetConnected() {
    this.appointmentTypeSelectTarget.addEventListener("change", () => this.applyDurationFromSelect())
  }

  applyDurationFromSelect() {
    if (!this.hasAppointmentTypeSelectTarget) return
    const select = this.appointmentTypeSelectTarget
    const option = select.options[select.selectedIndex]
    const minutes = parseInt(option?.dataset?.durationMinutes ?? option?.getAttribute?.("data-duration-minutes"), 10)
    if (!isNaN(minutes)) {
      this.durationMinutesValue = minutes
      this.updateEndTime()
    }
  }

  // Called when start time input changes
  updateEndTime() {
    const start = this.startTimeValue
    if (!start || !this.durationMinutesValue) {
      this.setEndTimeDisplay("—")
      return
    }
    const [h, m] = start.split(":").map((x) => parseInt(x, 10) || 0)
    const totalMins = h * 60 + m + this.durationMinutesValue
    const endMins = totalMins % 1440
    const eh = Math.floor(endMins / 60)
    const em = endMins % 60
    const formatted = this.formatTime(eh, em)
    this.setEndTimeDisplay(formatted)
  }

  formatTime(hour24, min) {
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12
    const ampm = hour24 < 12 ? "AM" : "PM"
    const m = String(min).padStart(2, "0")
    return `${hour12}:${m} ${ampm}`
  }

  setEndTimeDisplay(text) {
    if (this.hasEndTimeDisplayTarget) {
      this.endTimeDisplayTarget.textContent = text
    }
  }

  get startTimeValue() {
    return this.hasStartTimeTarget ? this.startTimeTarget.value?.trim() : null
  }

  // Fired by forms--combobox when a selection is made
  memberSelected(event) {
    const { id } = event.detail || {}
    if (id) {
      if (this.hasMemberFieldsTarget) {
        this.memberFieldsTarget.classList.add("hidden")
        this.memberFieldsTarget.querySelectorAll("input").forEach((i) => (i.disabled = true))
      }
    } else {
      this.clearMemberSelection()
    }
  }

  memberIdChanged(event) {
    if (!event.target.name?.includes("member_id")) return
    if (!event.target.value) {
      this.clearMemberSelection()
    }
  }

  clearMemberSelection() {
    if (this.hasMemberFieldsTarget) {
      this.memberFieldsTarget.classList.remove("hidden")
      this.memberFieldsTarget.querySelectorAll("input").forEach((i) => (i.disabled = false))
    }
  }
}
